import * as React from 'react';
import classNames from 'classnames';
import { forwardRef, HTMLAttributes } from 'react';

export interface ChipProps extends HTMLAttributes<HTMLDivElement> {
    label: string;
    disabled?: boolean;
    buttonClose?: boolean;
    image?: string;
    fixedWidth?: boolean;
    selected?: boolean;
    dragged?: boolean;
    buttonPair?: boolean;
    onClose?: (ev?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

/**
 * @name    a-chip
 * @type    atom
 * @param   {string} label          Label of Chip
 * @param   {boolean} disabled      Disable Chip
 * @param   {boolean} buttonClose   Show Close Button
 * @param   {string} image          URL of Image to show
 * @param   {boolean} fixedWidth    Set Chip to have a fixed Width
 * @param   {boolean} selected      Set Chip to selected State
 * @param   {boolean} dragged       Set Chip to dragged State
 * @param   {boolean} buttonPair    Render two Chips (for Preview-Purposes only)
 * @description
 * representation of chip elements
 */
export const Chip = forwardRef<HTMLDivElement, ChipProps>((props, ref) => {
    const {
        label,
        disabled,
        buttonClose,
        image,
        fixedWidth,
        selected,
        dragged,
        buttonPair,
        className,
        onClose,
        ...rest
    } = props;
    const elementClass = classNames(
        'a-chip',
        {
            'a-chip--fixed': fixedWidth,
            '-disabled': disabled,
            '-btnClose': buttonClose,
            '-image': image,
            '-selected': selected,
            '-dragged': dragged,
        },
        className,
    );

    const imageMarkup = (
        <div
            className="a-chip__image"
            style={{ backgroundImage: `url('${image}')` }}
        />
    );
    const chipLabelId = 'chip-label-id';
    const labelMarkup = (
        <span id={chipLabelId} className="a-chip__label">
            {label}
        </span>
    );

    let imageLabelMarkup;
    if (image && fixedWidth) {
        imageLabelMarkup = (
            <div className="fixed-width-image-label-group">
                {imageMarkup}
                {labelMarkup}
            </div>
        );
    } else {
        imageLabelMarkup = (
            <>
                {image && imageMarkup}
                {labelMarkup}
            </>
        );
    }

    function handleOnClose(ev: React.MouseEvent<HTMLElement, MouseEvent>) {
        onClose?.(ev);
    }

    const closeButtonMarkup = (
        <div className="a-chip__close" onClick={handleOnClose} />
    );

    const chipMarkup = (
        <div
            ref={ref}
            className={elementClass}
            role="button"
            aria-labelledby={chipLabelId}
            {...rest}
        >
            {imageLabelMarkup}
            {buttonClose && closeButtonMarkup}
        </div>
    );

    return (
        <>
            {chipMarkup}
            {buttonPair && chipMarkup}
        </>
    );
});
